import { Injectable } from '@angular/core';
import { 
  Auth, 
  authState, 
  createUserWithEmailAndPassword, 
  FacebookAuthProvider, 
  getAuth, 
  GoogleAuthProvider, 
  onAuthStateChanged, 
  sendEmailVerification, 
  sendPasswordResetEmail, 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  updateEmail, 
  updateProfile, 
  User, 
  UserCredential 
} from '@angular/fire/auth';
import { DocumentReference, Firestore, collectionData, doc, docData, getDoc, onSnapshot, setDoc, updateDoc,  } from '@angular/fire/firestore';
import { BehaviorSubject, forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { RegistrationRequest } from '../../models/registration-request';
import { AuthenticationRequest } from '../../models/authentication-request';
import { ref } from 'firebase/storage';
import { query } from 'express';
import { collection } from 'firebase/firestore/lite';
import { Timestamp, where } from '@firebase/firestore';

@Injectable({
  providedIn: 'root'
})
export class AuthServiceService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();
  private lastSignInTime: number | null = null; // Store last sign-in timestamp
  constructor(
    private firebaseAuth: Auth,
    private firestore: Firestore,
    private http:HttpClient
  ) {
    this.checkAuthState();
  }

 register(request: RegistrationRequest): Observable<UserCredential> {
  return from(createUserWithEmailAndPassword(this.firebaseAuth, request.email, request.password)).pipe(
    switchMap((response) => {
      console.log("✅ Registration successful:", response);
      const { user } = response;

      // Update user profile with display name
      return from(updateProfile(user, { displayName: request.fullName })).pipe(
        tap(() => console.log("✅ Display name updated for:", request.fullName)),
        switchMap(() => {
          // Create user document in Firestore
          const userRef = doc(this.firestore, `users/${user.uid}`);
          return from(setDoc(userRef, {
            email: request.email,
            fullName: request.fullName,
            role: request.role,
            uid: user.uid,
            createdAt: new Date(),
            image:"assets/img/user.png",
            phoneCode:"",
            country:"",
            accountLocked:false
          })).pipe(
            tap(() => console.log("✅ Firestore user document created for UID:", user.uid))
          );
        }),
        switchMap(() => {
          console.log("📧 Sending verification email now...");
          return this.sendVerificationEmail();
        }),
        tap(() => console.log("📧 Verification email send function completed.")),
        map(() => response)
      );
    }),
    catchError((error) => {
      console.error("❌ Registration Error:", error);
      throw error;
    })
  );
}

sendVerificationEmail(): Observable<void> {
  console.log("📧 sendVerificationEmail() called");
  console.log("👤 Current user:", this.firebaseAuth.currentUser);

  if (this.firebaseAuth.currentUser) {
    return from(sendEmailVerification(this.firebaseAuth.currentUser)).pipe(
      tap(() => console.log("✅ Email verification sent successfully.")),
      catchError((error) => {
        console.error("❌ Email Verification Error:", error);
        throw error;
      })
    );
  }

  console.warn("⚠️ No user is signed in — cannot send verification email.");
  return from(Promise.reject("No user is signed in."));
}



  login(request:AuthenticationRequest): Observable<UserCredential> {
    return from(signInWithEmailAndPassword(this.firebaseAuth,request.email,request.password)).pipe(
      map((response) => {  return response;  }),
      catchError((error) => {
        console.error("Login Error:", error);
        throw error;
      })
    );
  }
  restPassword(email:string):Observable<void>{
    return from(sendPasswordResetEmail(this.firebaseAuth,email))
  }


  private checkAuthState() {
    onAuthStateChanged(this.firebaseAuth, (user) => {
      if (user) {
        this.currentUserSubject.next(user);
      } else {
        this.currentUserSubject.next(null);
      }
    });
  }


 // ✅ Check if user is logged in and email is verified
  isUserLoggedIn(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      onAuthStateChanged(this.firebaseAuth, (user) => {
        if (user) {
          // Check if the email is verified
          if (user.emailVerified) {
            observer.next(true); // User is logged in and email is verified
          } else {
            observer.next(false); // User is logged in but email is not verified
          }
        } else {
          observer.next(false); // No user is logged in
        }
      });
    });
  }

  signOut(): Observable<void> {
    return from(this.firebaseAuth.signOut()).pipe(
      map(() => {
      }),
      catchError((error) => {
        console.error("Sign Out Error:", error);
        throw error;
      })
    );
  }


  updateUser(uid: string,email:string,fullName:string,photoURL?:string): Observable<void> {
    const user = this.firebaseAuth.currentUser;

    if (!user) {
      return from(Promise.reject("❌ No authenticated user found!"));
    }
    console.log("user",user)
    console.log("uid",uid)

    const updates: Promise<void>[] = [];

    // ✅ Update Display Name & Photo in Firebase Authentication
    if (fullName || photoURL) {
      updates.push(updateProfile(user, {
        displayName: fullName || user.displayName,
        photoURL: photoURL || user.photoURL
      }));
    }

    // ✅ Update Email in Firebase Authentication
    if (email && email !== user.email) {
      updates.push(updateEmail(user, email));
    }

    // ✅ Update Firestore Database
    const userRef = doc(this.firestore, `users/${uid}`);
    updates.push(updateDoc(userRef, {email, fullName}));

    // ✅ Fix: Use `map(() => void)` to return a single `void`
    return from(Promise.all(updates)).pipe(map(() => void 0));
  }


 isAdmin(): Observable<boolean> {
  return authState(this.firebaseAuth).pipe(
    switchMap(user => {
      if (!user) return of(false);
      const userDocRef = doc(this.firestore, `users/${user.uid}`);
      return docData(userDocRef).pipe(
        map(userDoc => userDoc?.["role"] === 'ADMIN')
      );
    })
  );
}


  getCurrentUserUid(): string | null {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (user) {
      return user.uid; // return the UID
    } else {
      return null; // no user logged in
    }
  
  }
  toggleUserBlock(uid: string): Observable<void> {
    const userRef = doc(this.firestore, `users/${uid}`);
    return from(getDoc(userRef)).pipe(
      switchMap((snapshot) => {
        if (!snapshot.exists()) {
          throw new Error(`❌ User with UID ${uid} not found.`);
        }

        const currentStatus = snapshot.data()?.["accountLocked"] ?? false;
        const newStatus = !currentStatus;

        // Update Firestore
        const firestoreUpdate = updateDoc(userRef, { accountLocked: newStatus });

        // Call backend API to update Authentication (requires Admin SDK)
        const authUpdate = this.http.post<void>(`/api/auth/disableUser`, { uid, disabled: newStatus }).pipe(
          catchError((error) => {
            console.error("❌ Error updating Authentication:", error);
            throw error;
          })
        );

        return forkJoin([from(firestoreUpdate), authUpdate]).pipe(
          tap(() => {
            if (newStatus) {
              console.log(`🚫 User with UID ${uid} has been blocked.`);
            } else {
              console.log(`✅ User with UID ${uid} has been unblocked.`);
            }
          }),
          map(() => void 0)
        );
      }),
      catchError((error) => {
        console.error("❌ Error toggling user block status:", error);
        throw error;
      })
    );
  }

  // Google login
loginWithGoogle(): Observable<UserCredential> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' }); // 🔥 Force account chooser

  return from(signInWithPopup(this.firebaseAuth, provider)).pipe(
    tap((res) => console.log('✅ Google login successful', res)),
    catchError((error) => {
      console.error('❌ Google login error', error);
      throw error;
    })
  );
}


// Facebook login
loginWithFacebook(): Observable<UserCredential> {
  const provider = new FacebookAuthProvider();
  return from(signInWithPopup(this.firebaseAuth, provider)).pipe(
    tap((res) => console.log('✅ Facebook login successful', res)),
    catchError((error) => {
      console.error('❌ Facebook login error', error);
      throw error;
    })
  );
}


handleSocialLogin(user: User): Observable<void> {
  const userRef = doc(this.firestore, `users/${user.uid}`);
  return from(getDoc(userRef)).pipe(
    switchMap((snapshot) => {
      if (!snapshot.exists()) {
        return from(setDoc(userRef, {
          uid: user.uid,
          email: user.email,
          fullName: user.displayName || 'User',
          role: 'USER',
          createdAt: new Date(),
          image: user.photoURL || 'assets/img/user.png',
          accountLocked: false,
          phoneCode:"",
          country:"",
        }));
      }
      return of(void 0);
    })
  );
}



}
